import { Undefinable } from 'providence-types';
import { createReducer, on } from '../../core/utils/store';
import {
  AccountingDetails,
  JurisdictionDetails,
  PayloadAndState,
  ProjectDetails,
  ProjectManagementDetails,
  SiteBrowse,
  SOFToolDetails,
} from '../../models/interfaces';
import { ProjectDetailsWithoutCategories } from '../../models/types';
import { BreadcrumbsActions } from '../actions/breadcrumbs.actions';

export interface BreadcrumbsState {
  breadcrumbs: Record<string, Undefinable<string | number>>;
}

const initialState: BreadcrumbsState = {
  breadcrumbs: {},
};

export const reducer = createReducer(
  initialState,
  on(
    BreadcrumbsActions.site,
    ({
      payload: { siteNumber },
    }: PayloadAndState<SiteBrowse, BreadcrumbsState>) => ({
      breadcrumbs: {
        siteNumber,
      },
    }),
  ),
  on(
    BreadcrumbsActions.project,
    ({
      payload: {
        site: { siteNumber },
        projectBundleId,
      },
    }: PayloadAndState<ProjectDetailsWithoutCategories, BreadcrumbsState>) => ({
      breadcrumbs: {
        siteNumber,
        projectBundleId,
      },
    }),
  ),
  on(
    BreadcrumbsActions.Engineering.engineering,
    ({
      payload: {
        site: { siteNumber },
        projectBundleId,
      },
    }: PayloadAndState<
      Omit<ProjectDetailsWithoutCategories, 'paceTasks'>,
      BreadcrumbsState
    >) => ({
      breadcrumbs: {
        siteNumber,
        projectBundleId,
      },
    }),
  ),
  on(
    BreadcrumbsActions.Engineering.SOFTool,
    ({
      payload: {
        generalInfo: {
          site: { siteNumber },
          projectBundleId,
        },
      },
    }: PayloadAndState<SOFToolDetails, BreadcrumbsState>) => ({
      breadcrumbs: {
        siteNumber,
        projectBundleId,
      },
    }),
  ),
  on(
    BreadcrumbsActions.Engineering.scoping,
    ({
      payload: {
        projectBundleId,
        site: { siteNumber },
      },
    }: PayloadAndState<
      Omit<ProjectDetailsWithoutCategories, 'RAD'>,
      BreadcrumbsState
    >) => ({
      breadcrumbs: {
        siteNumber,
        projectBundleId,
      },
    }),
  ),
  on(
    BreadcrumbsActions.Engineering.siteAudit,
    ({
      payload: {
        projectBundleId,
        site: { siteNumber },
      },
    }: PayloadAndState<ProjectDetailsWithoutCategories, BreadcrumbsState>) => ({
      breadcrumbs: {
        siteNumber,
        projectBundleId,
      },
    }),
  ),
  on(
    BreadcrumbsActions.leasing,
    ({
      payload: {
        projectBundleId,
        site: { siteNumber },
      },
    }: PayloadAndState<
      Omit<ProjectDetailsWithoutCategories, 'paceTasks'>,
      BreadcrumbsState
    >) => ({
      breadcrumbs: {
        siteNumber,
        projectBundleId,
      },
    }),
  ),
  on(
    BreadcrumbsActions.Permitting.permitting,
    ({
      payload: {
        projectBundleId,
        site: { siteNumber },
      },
    }: PayloadAndState<ProjectDetails, BreadcrumbsState>) => ({
      breadcrumbs: {
        siteNumber,
        projectBundleId,
      },
    }),
  ),
  on(
    BreadcrumbsActions.Permitting.jurisdiction,
    ({
      payload: { jurisdictionName },
    }: PayloadAndState<JurisdictionDetails, BreadcrumbsState>) => ({
      breadcrumbs: { jurisdictionName },
    }),
  ),
  on(
    BreadcrumbsActions.projectManagement,
    ({
      payload: {
        projectBundleId,
        site: { siteNumber },
      },
    }: PayloadAndState<
      Omit<ProjectManagementDetails, 'categories' | 'paceTasks'>,
      BreadcrumbsState
    >) => ({
      breadcrumbs: { projectBundleId, siteNumber },
    }),
  ),
  on(
    BreadcrumbsActions.accounting,
    ({
      payload: {
        projectData: {
          projectBundleId,
          site: { siteNumber },
        },
      },
    }: PayloadAndState<AccountingDetails, BreadcrumbsState>) => ({
      breadcrumbs: { projectBundleId, siteNumber },
    }),
  ),
);
